import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { debounceTime, Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { GroupedBarChartComponent } from '../../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { MaintenanceInsightsResponse } from '../../../models/maintenance-dashboard-response.model';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatCost } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountAssetScopeSelectorComponent, ScopeSelection } from '../../../../../../_shared/components/account-asset-scope-selector/account-asset-scope-selector.component';
import { PreventiveVsReactiveRequest } from '../../../models/maintenance-dashboard-request.model';


@Component({
  selector: 'app-preventive-vs-reactive-modal',
  standalone: true,
  imports: [CommonModule, GroupedBarChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, AccountAssetScopeSelectorComponent, DateFilterComponent, TranslocoPipe],
  templateUrl: './preventive-vs-reactive-modal.component.html',
  styleUrl: './preventive-vs-reactive-modal.component.scss'
})
export class PreventiveVsReactiveModalComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);

  readonly PREVENTIVE_SERIES = 'Preventive';
  readonly REACTIVE_SERIES = 'Reactive';
  chartData: GroupedChartData[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  subgroups = this.buildSubgroups();

  isLoading: boolean = true;
  title: string = this.transloco.translate('maintenanceDashboard.charts.preventiveVsReactive');
  accountFilter: AccountFilterData;
  selectedAssets: ScopeSelection[] = [];
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  currencySymbol: string = '';
  // The account and asset pickers emit on every tick, so wait for the clicks to settle before reloading
  private readonly filterReloadDebounceMs = 300;
  private cancelSubject: Subject<void> = new Subject<void>();
  private filterChanged: Subject<void> = new Subject<void>();

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;

    // Legend and title are resolved once, so refresh them when the language changes.
    this.transloco.selectTranslation()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.subgroups = this.buildSubgroups();
        this.title = this.transloco.translate('maintenanceDashboard.charts.preventiveVsReactive');
      });

    this.filterChanged
      .pipe(debounceTime(this.filterReloadDebounceMs), takeUntilDestroyed())
      .subscribe(() => void this.loadMaintenanceData());
  }

  private buildSubgroups(): Array<{ name: string; legend: string; color: string }> {
    return [
      { name: this.PREVENTIVE_SERIES, legend: this.transloco.translate('maintenanceDashboard.charts.preventive'), color: '#66AECF' },
      { name: this.REACTIVE_SERIES, legend: this.transloco.translate('maintenanceDashboard.charts.reactive'), color: '#F4BC2B' }
    ];
  }

  // The first load waits for the account and date filters, which both emit once they initialise
  ngOnInit(): void {
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.authService.getUserSettings()?.currencyCode ?? 'ZAR');
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    // The asset picker lists only the selected accounts' assets, so earlier picks may no longer belong
    this.selectedAssets = [];
    this.updateFilter();
  }

  onAccountFilterLoadFailed(): void {
    this.isLoading = false;
  }

  onAssetScopesChange(scopes: ScopeSelection[]): void {
    this.selectedAssets = scopes;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterChanged.next();
    }
  }

  async loadMaintenanceData(): Promise<void> {
    // A newer filter change supersedes any in-flight load, so drop it to stop a slower, older response winning
    this.cancelSubject.next();
    this.isLoading = true;

    try {
      const request: PreventiveVsReactiveRequest = {
        accountIds: this.accountFilter.accountIds ?? [],
        assetIds: this.selectedAssets.map(asset => asset.id),
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        interval: this.dateFilter.interval,
      };

      const [summaryResponse, preventiveResponse, reactiveResponse] = await Promise.all([
        this.dashboardService.getPreventiveVsReactiveSummary({
          ...request,
          previousTimeFrom: this.dateFilter.previousCycleStartDate!,
          previousTimeTo: this.dateFilter.previousCycleEndDate!
        }, this.cancelSubject),
        this.dashboardService.getPreventiveRatio(request, this.cancelSubject),
        this.dashboardService.getReactiveRatio(request, this.cancelSubject)
      ]);

      this.totalValue = summaryResponse.data?.totalValue ?? 0;
      this.percentageChange = summaryResponse.data?.percentageChange ?? 0;
      this.chartData = this.processResponses(preventiveResponse.data ?? [], reactiveResponse.data ?? []);
      this.yTicks = this.calculateYTicks(this.chartData);

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processResponses(preventiveData: MaintenanceInsightsResponse[], reactiveData: MaintenanceInsightsResponse[]): GroupedChartData[] {
    if (!preventiveData && !reactiveData) return [];

    const daysDifference = differenceInDays(this.dateFilter.endDate!, this.dateFilter.startDate!);

    const datasets = [
      { data: preventiveData, label: this.PREVENTIVE_SERIES },
      { data: reactiveData, label: this.REACTIVE_SERIES }
    ];

    let groupedData: GroupedChartData[];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day (no limit for modal)
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    }

    return groupedData;
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item[this.PREVENTIVE_SERIES]) || 0,
      Number(item[this.REACTIVE_SERIES]) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks, 6);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return true; // Preventive maintenance increase is positive
  }

  formatValue(): string {
    return formatCost(this.totalValue, this.currencySymbol);
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}